import { useEffect, useState } from "react";
import { Cell, ResponsiveContainer } from "recharts";
import { BarChart, Bar, XAxis, YAxis, Tooltip} from "recharts";
import anketaService from "../../services/anketa.service";
import popunjeniOdgovorService from "../../services/popunjeniOdgovor.service";
import "./OdgovoriNaPitanjaChart.css";



const OdgovoriNaPitanjaChart = ({ anketaId }) => {
  const [anketa, setAnketa] = useState(null);
  const [pitanja, setPitanja] = useState([]);
  const [odgovori, setOdgovori] = useState({});
  const [bojeOdgovora, setBojeOdgovora] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const generateRandomColor = () => {
    let randomColor;
    let r, g, b;
    do {
      randomColor = `#${Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0")}`;
      r = parseInt(randomColor.slice(1, 3), 16);
      g = parseInt(randomColor.slice(3, 5), 16);
      b = parseInt(randomColor.slice(5, 7), 16);
    } while (randomColor === "#000000" || (r < 50 && g < 50 && b < 50));
    return randomColor;
  };

  useEffect(() => {
    if (!anketaId || isNaN(anketaId)) {
      setError("Nije pronađen ID ankete");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const anketaResponse = await anketaService.getById(anketaId);
        if (!anketaResponse.data) throw new Error("Anketa nije pronađena");
        setAnketa(anketaResponse.data);

        const pitanjaResponse = await anketaService.getPitanjaSaOdgovorima(
          anketaId
        );
        const pitanjaData = pitanjaResponse.data || [];

        const neTekstualnaPitanja = pitanjaData.filter(
          (p) => p.tip !== "TEKSTUALNO"
        );
        setPitanja(neTekstualnaPitanja);

        const odgovoriResponses = await Promise.all(
          neTekstualnaPitanja.map(async (pitanje) => {
            try {
              const pitanjeId = pitanje.id_pitanje || pitanje.id;
              const odgovori = await popunjeniOdgovorService.getAllByPitanjeId(
                pitanjeId
              );
              return { pitanjeId: pitanjeId, odgovori: odgovori.data };
            } catch (error) {
              return {
                pitanjeId: pitanje.id_pitanje || pitanje.id,
                odgovori: [],
              };
            }
          })
        );

        const sviOdgovori = odgovoriResponses.reduce(
          (acc, { pitanjeId, odgovori }) => {
            acc[pitanjeId] = odgovori;
            return acc;
          },
          {}
        );
        setOdgovori(sviOdgovori);
        const boje = {};
        neTekstualnaPitanja.forEach((pitanje) => {
          const pitanjeId = pitanje.id_pitanje || pitanje.id;
          const odgovoriNaPitanje = sviOdgovori[pitanjeId] || [];
          (pitanje.ponudjeniOdgovori || []).forEach((odgovor) => {
            const answerText =
              odgovor.tekstOdgovora ||
              odgovor.tekst_odgovora ||
              odgovor.odgovor_tekst;
            if (answerText && !boje[answerText]) {
              boje[answerText] = generateRandomColor();
            }
          });
          const uniqueOdgovorTekstovi = [
            ...new Set(
              odgovoriNaPitanje.map(
                (o) => o.odgovorTekst || o.odgovor_tekst || ""
              )
            ),
          ];
          uniqueOdgovorTekstovi.forEach((answerText) => {
            if (answerText && !boje[answerText]) {
              boje[answerText] = generateRandomColor();
            }
          });
          if (pitanje.tip === "DA_NE") {
            if (!boje["Da"]) boje["Da"] = generateRandomColor();
            if (!boje["Ne"]) boje["Ne"] = generateRandomColor();
          }
        });

        setBojeOdgovora(boje);
      } catch (error) {
        setError("Došlo je do greške pri učitavanju podataka");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [anketaId]);

  if (loading)
    return <div className="loading-error-message">Učitavanje podataka...</div>;
  if (error) return <div className="loading-error-message error">{error}</div>;

  return (
    <div className="anketa-statistika-div">
      {anketa && (
        <h2 className="anketa-statistika-h2">Naziv ankete: {anketa.naziv}</h2>
      )}

      {pitanja.length > 0 ? (
        pitanja
          .sort((a, b) => a.pozicija - b.pozicija)
          .map((pitanje) => (
            <div
              key={pitanje.id_pitanje || pitanje.id}
              className="pitanje-container"
            >
              <div className="pitanje-tekst">
                <h4 className="pitanje-statistika-h4">
                  {pitanje.pozicija}.{" "}
                  {pitanje.tekstPitanja ||
                    pitanje.tekst_pitanja ||
                    "Nepoznato pitanje"}
                </h4>
              </div>

              <StatsRenderer pitanje={pitanje} bojeOdgovora={bojeOdgovora} />
            </div>
          ))
      ) : (
        <p className="loading-error-message">
          Nema pitanja sa ponuđenim odgovorima za prikaz statistike.
        </p>
      )}
    </div>
  );
};

const StatsRenderer = ({ pitanje, bojeOdgovora }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const statsData = await getAnswerStats(pitanje);
      setStats(statsData);
      setLoading(false);
    };

    fetchStats();
  }, [pitanje]);

  if (loading)
    return <p className="loading-error-message">Učitavanje statistike...</p>;

  const hasAnswers = stats.some((s) => s.value > 0);

  return hasAnswers ? (
    <div className="chart-legenda-container">
      <div className="anketa-chart-wrapper">
        <div className="responsive-chart-container">
          <div className="chart-inner-wrapper">
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={stats}
                margin={{ top: 20, right: 0, left: 0, bottom: 70 }}
              >
                <XAxis
                  dataKey="name"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  tickFormatter={(value) =>
                    value.length > 10 ? value.slice(0, 10) + "…" : value
                  }
                />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value">
                  {stats.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={bojeOdgovora[entry.name] || "#ccc"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="legenda-wrapper">
        {stats.map((entry, index) => (
          <div key={index} className="legenda-item">
            <span
              className="legenda-dot"
              style={{
                backgroundColor: bojeOdgovora[entry.name] || "#ccc",
              }}
            />
            <span className="legenda-text">
              {entry.name}: {entry.value} odgovora
            </span>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <p className="loading-error-message">Nema odgovora na ovo pitanje.</p>
  );
};

const getAnswerStats = async (pitanje) => {
  if (!pitanje) return [];

  const pitanjeId = pitanje.id_pitanje || pitanje.id;
  const odgovoriResponse = await popunjeniOdgovorService.getAllByPitanjeId(
    pitanjeId
  );
  const odgovoriNaPitanje = odgovoriResponse.data || [];

  const questionText =
    pitanje.tekstPitanja || pitanje.tekst_pitanja || "Nepoznato pitanje";
  if (pitanje.tip === "DA_NE") {
    const daCountResponse =
      await popunjeniOdgovorService.getCountByPitanjeIdAndTekst(
        pitanjeId,
        "Da"
      );
    const neCountResponse =
      await popunjeniOdgovorService.getCountByPitanjeIdAndTekst(
        pitanjeId,
        "Ne"
      );
    const stats = [
      { name: "Da", value: daCountResponse.data || 0 },
      { name: "Ne", value: neCountResponse.data || 0 },
    ];
    return stats;
  }

  const odgovorMap = new Map();
  odgovoriNaPitanje.forEach((o) => {
    const originalText = o.odgovorTekst || o.odgovor_tekst || "";
    const normalizedText = originalText.trim().toLowerCase();
    if (!normalizedText) return;

    if (!odgovorMap.has(normalizedText)) {
      odgovorMap.set(normalizedText, { name: originalText, value: 1 });
    } else {
      odgovorMap.get(normalizedText).value += 1;
    }
  });

  (pitanje.ponudjeniOdgovori || []).forEach((po) => {
    const text =
      po.tekstOdgovora || po.tekst_odgovora || po.odgovor_tekst || "";
    const normalized = text.trim().toLowerCase();
    if (text && !odgovorMap.has(normalized)) {
      odgovorMap.set(normalized, { name: text, value: 0 });
    }
  });

  const stats = Array.from(odgovorMap.values());
  return stats;
};

export default OdgovoriNaPitanjaChart;
